'use client';

import { c as _c } from "react/compiler-runtime";
import { animate, useReducedMotion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { AppImage } from '@/app/components/media/app-image';
import { useIsWebGPUSupported } from '@/app/hooks/useIsWebGPUSupported';
import { useWebGPUFallbackMode } from '@/app/hooks/useWebGPUFallbackMode';
import { BlackLayer } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/black-layer';
import { LoadingVeil } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/loading-veil';
import { useHeroPreload } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/loading-veil/useHeroPreload';
import { SectionResetVeil } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/section-reset-veil';
import { SectionSlider } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/section-slider';
import { BACKGROUND_IMAGE_FALLBACK_SRC, BACKGROUND_IMAGE_SRC, INTRO_INDEX, SPARTAN_ENGINE_SECTION_COUNT, SPARTAN_ENGINE_SECTION_IDS, STORY_INDEX } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/section-slider/constants';
import { useVerticalSectionStepper } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/section-slider/useVerticalSectionStepper';
import { probeAutoplay } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/autoplayProbe';
import { useHeroClockStart } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/pageClock';
import { useHeroClipQueueRunning } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/preload-queue/clipQueue';
import type { SectionPhase } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/types';
import { createEngineAnchor, type EngineAnchor } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useEngineAnchor';
import { useHeroBehaviourTracking } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useHeroBehaviourTracking';
import { createSectionVideoProgress } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useSectionVideoProgress';
import { SECTION_FULL_HEIGHT_CLASSNAME } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/styles';
import { SPARTAN_ENGINE_HERO_BEHAVIOUR, SPARTAN_ENGINE_HERO_TRACKING } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/tracking';
import { EngineBackdrop } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/backdrop';
import { BACKDROP_FADE_IN_SECONDS, BLACK_COVER_FADE_SECONDS, BLACK_STAGE_FADE_SECONDS, INTRO_LIGHTNING_SPEED, SECTION_ATMOSPHERE_FADE_OUT_SECONDS, STORY_CLIPS_DELAY_SECONDS } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/constants';
import { EngineStage } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/engine-stage';
import { EngineAirflow } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/engine-stage/airflow';
import { ENGINE_AIRFLOW_CONFIG } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/engine-stage/airflow/config';
import { EngineAura } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/engine-stage/aura';
import { EngineLightning } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/engine-stage/lightning';
import { LIGHTNING_AUTHORED_CONFIG } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/engine-stage/lightning/config/authored';
import type { EngineBlackMode, SectionPlayMode } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/types';
import { SPARTAN_ENGINE_HERO_BANNER_SECTION_ID } from '@/app/lcv/components/sections/spartan/engine/shared/section-anchors';
import { SpartanStableViewport } from '@/app/lcv/components/sections/spartan/shared/stable-viewport';
import { SpartanWebGPUCanvasBasic } from '@/app/lcv/components/sections/spartan/shared/webgpu/canvas-basic';
import { useMutableRef } from '@/app/lcv/components/sections/spartan/shared/webgpu/hooks/useMutableRef';
import { isTouchDevice } from '@/app/utils/browser';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { reportRumWarning } from '@/app/utils/reportRum';
const INTRO_STRIKE_HOLD_AFTER_SETTLE_MS = 2000;

// Owns the complete hero lifecycle: preload, slide transitions and the
// WebGPU / fallback decision.
export const HeroAnimation = () => {
  const $ = _c(144);
  const gpuStatus = useIsWebGPUSupported();
  const {
    isFallbackMode
  } = useWebGPUFallbackMode();
  const reducedMotion = useReducedMotion();
  const clockStartMs = useHeroClockStart();
  const {
    isReady: isHeroReady,
    reportAssetLoaded
  } = useHeroPreload(clockStartMs);
  const [playBlockedVisit, setPlayBlockedVisit] = useState(null);
  const [autoplayCheckedVisit, setAutoplayCheckedVisit] = useState(null);
  const [introReveal, setIntroReveal] = useState(0);
  const [videoEndedGeneration, setVideoEndedGeneration] = useState(null);
  const [blackMode, setBlackMode] = useState("none");
  const [storyVideoGeneration, setStoryVideoGeneration] = useState(0);
  const [haveStoryClipsFinished, setHaveStoryClipsFinished] = useState(false);
  const [isIntroStrikeHeld, setIsIntroStrikeHeld] = useState(false);
  const [isHotspotPopupOpen, setIsHotspotPopupOpen] = useState(false);
  const [hasSceneCompiled, setHasSceneCompiled] = useState(false);
  const [backdropGeneration, setBackdropGeneration] = useState(0);
  const introFloatAnimationRef = useRef(null);
  const storyBreathingAnimationRef = useRef(null);
  const [backdropBurstKey, setBackdropBurstKey] = useState(0);
  const lastBurstGenerationRef = useRef(-1);
  const [auraResetKey, setAuraResetKey] = useState(0);
  const [hasBackdropBeenShown, setHasBackdropBeenShown] = useState(false);
  const strikeHoldTimerRef = useRef(null);
  const backdropVisibilityRef = useMutableRef(_temp);
  const engineAuraVisibilityRef = useMutableRef(_temp2);
  const videoProgressRef = useMutableRef(createSectionVideoProgress);
  const engineAnchorRef = useMutableRef(_temp3);
  const shouldRenderFallback = gpuStatus === "unsupported" || isFallbackMode;
  const gestureSurfaceRef = useRef(null);
  const backdropWriteRef = useRef(backdropVisibilityRef);
  const introRevealRef = useRef(introReveal);
  const canvasBoxRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      initialInView: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    ref: sectionRef,
    inView: isSectionInView
  } = useInView(t0);
  const {
    activeIndex,
    exitingIndex,
    playMode,
    isLocked,
    isSkipCovered,
    generation,
    reportAnimationEnd,
    reportExitEnd,
    goTo,
    skip: t1
  } = useVerticalSectionStepper({
    slideCount: SPARTAN_ENGINE_SECTION_COUNT,
    containerRef: gestureSurfaceRef,
    onEnterSlide: (index, mode, source) => {
      trackSlideEnter(index, source);
      if (SPARTAN_ENGINE_SECTION_IDS[index] === "intro") {
        handleIntroArrival(mode);
      }
    },
    onBlockedGesture: (index_0, input) => {
      trackBlockedGesture(index_0, input);
      if (isPlayPromptShown) {
        skip();
      }
    }
  });
  const skip = t1;
  const t2 = shouldRenderFallback ? SPARTAN_ENGINE_HERO_BEHAVIOUR.renderMode.fallback : SPARTAN_ENGINE_HERO_BEHAVIOUR.renderMode.webgpu;
  let t3;
  if ($[1] !== activeIndex || $[2] !== isHeroReady || $[3] !== isLocked || $[4] !== t2) {
    t3 = {
      isReady: isHeroReady,
      isLocked,
      activeIndex,
      renderMode: t2
    };
    $[1] = activeIndex;
    $[2] = isHeroReady;
    $[3] = isLocked;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    trackSlideEnter: t4,
    trackBlockedGesture: t5,
    trackSkip
  } = useHeroBehaviourTracking(t3);
  const trackSlideEnter = t4;
  const trackBlockedGesture = t5;
  const handleSkip = () => {
    trackSkip();
    skip();
  };
  let t6;
  if ($[6] !== activeIndex || $[7] !== exitingIndex) {
    t6 = index_1 => {
      if (exitingIndex === index_1) {
        return "exiting";
      }
      return activeIndex === index_1 ? "active" : "idle";
    };
    $[6] = activeIndex;
    $[7] = exitingIndex;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const phaseOf = t6;
  const activeSectionId = SPARTAN_ENGINE_SECTION_IDS[activeIndex];
  const activeVisitKey = `${activeIndex}:${generation[activeIndex]}`;
  const isPlayPromptShown = playMode === "full" && playBlockedVisit === activeVisitKey;
  let t7;
  let t8;
  if ($[9] !== activeIndex || $[10] !== activeVisitKey || $[11] !== playMode) {
    t7 = () => {
      if (playMode !== "full") {
        return;
      }
      if (!isTouchDevice) {
        setAutoplayCheckedVisit(activeVisitKey);
        return;
      }
      let cancelled = false;
      probeAutoplay().then(result => {
        if (cancelled) {
          return;
        }
        setAutoplayCheckedVisit(activeVisitKey);
        if (result !== "blocked") {
          return;
        }
        setPlayBlockedVisit(activeVisitKey);
        reportRumWarning("spartan_hero_autoplay_blocked", {
          section: SPARTAN_ENGINE_SECTION_IDS[activeIndex],
          source: "probe"
        });
      });
      return () => {
        cancelled = true;
      };
    };
    t8 = [activeVisitKey, activeIndex, playMode];
    $[9] = activeIndex;
    $[10] = activeVisitKey;
    $[11] = playMode;
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  useEffect(t7, t8);
  let t9;
  if ($[14] !== isPlayPromptShown) {
    t9 = () => {
      if (isPlayPromptShown) {
        setBlackMode("none");
      }
    };
    $[14] = isPlayPromptShown;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = isPlayPromptShown;
  let t11;
  if ($[16] !== t10) {
    t11 = [t10];
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  useEffect(t9, t11);
  let t12;
  if ($[18] !== activeIndex || $[19] !== activeVisitKey || $[20] !== playBlockedVisit) {
    t12 = index_2 => {
      if (index_2 !== activeIndex || playBlockedVisit === activeVisitKey) {
        return;
      }
      setPlayBlockedVisit(activeVisitKey);
      reportRumWarning("spartan_hero_autoplay_blocked", {
        section: SPARTAN_ENGINE_SECTION_IDS[index_2]
      });
    };
    $[18] = activeIndex;
    $[19] = activeVisitKey;
    $[20] = playBlockedVisit;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const handleVideoBlocked = t12;
  const isCanvasPaused = !isSectionInView || isHotspotPopupOpen;
  const [isSaveData, setIsSaveData] = useState(false);
  let t13;
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setIsSaveData(!!(navigator as Navigator & {
        connection?: {
          saveData?: boolean;
        };
      }).connection?.saveData);
    };
    t14 = [];
    $[22] = t13;
    $[23] = t14;
  } else {
    t13 = $[22];
    t14 = $[23];
  }
  useEffect(t13, t14);
  useHeroClipQueueRunning(isSectionInView && !isSaveData);
  const storyGeneration = generation[STORY_INDEX];
  const hasIntroVideoEnded = videoEndedGeneration === introReveal;
  const isIntroLightningLive = phaseOf(INTRO_INDEX) === "active" && hasIntroVideoEnded;
  const shouldShowBackdrop = phaseOf(INTRO_INDEX) === "active" && hasIntroVideoEnded || phaseOf(STORY_INDEX) === "active" && haveStoryClipsFinished;
  const isBackdropOwnerOnScreen = phaseOf(INTRO_INDEX) !== "idle" || phaseOf(STORY_INDEX) !== "idle";
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = mode_0 => {
      setBlackMode("none");
      const next = introRevealRef.current + 1;
      introRevealRef.current = next;
      setIntroReveal(next);
      if (mode_0 === "final") {
        setVideoEndedGeneration(next);
      }
    };
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const handleIntroArrival = t15;
  let t16;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => {
      if (strikeHoldTimerRef.current === null) {
        return;
      }
      clearTimeout(strikeHoldTimerRef.current);
      strikeHoldTimerRef.current = null;
    };
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  const clearStrikeHoldTimer = t16;
  let t17;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => {
      if (lastBurstGenerationRef.current !== introRevealRef.current) {
        lastBurstGenerationRef.current = introRevealRef.current;
        setBackdropBurstKey(_temp4);
      }
      clearStrikeHoldTimer();
      strikeHoldTimerRef.current = setTimeout(() => {
        strikeHoldTimerRef.current = null;
        setIsIntroStrikeHeld(false);
      }, INTRO_STRIKE_HOLD_AFTER_SETTLE_MS);
    };
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  const handleIntroSettled = t17;
  let t18;
  let t19;
  if ($[27] !== introReveal) {
    t18 = () => {
      introRevealRef.current = introReveal;
    };
    t19 = [introReveal];
    $[27] = introReveal;
    $[28] = t18;
    $[29] = t19;
  } else {
    t18 = $[28];
    t19 = $[29];
  }
  useEffect(t18, t19);
  const isIntroClipUnmounted = activeIndex === INTRO_INDEX && playMode === "final";
  let t20;
  let t21;
  if ($[30] !== isIntroClipUnmounted || $[31] !== reportAssetLoaded) {
    t20 = () => {
      if (!isIntroClipUnmounted) {
        return;
      }
      reportAssetLoaded("introVideo");
      reportAssetLoaded("videoCover");
    };
    t21 = [isIntroClipUnmounted, reportAssetLoaded];
    $[30] = isIntroClipUnmounted;
    $[31] = reportAssetLoaded;
    $[32] = t20;
    $[33] = t21;
  } else {
    t20 = $[32];
    t21 = $[33];
  }
  useEffect(t20, t21);
  let t22;
  let t23;
  if ($[34] !== isSectionInView) {
    t22 = () => {
      if (isSectionInView) {
        setAuraResetKey(_temp5);
      }
    };
    t23 = [isSectionInView];
    $[34] = isSectionInView;
    $[35] = t22;
    $[36] = t23;
  } else {
    t22 = $[35];
    t23 = $[36];
  }
  useEffect(t22, t23);
  let t24;
  let t25;
  if ($[37] !== shouldShowBackdrop) {
    t24 = () => {
      if (shouldShowBackdrop) {
        setHasBackdropBeenShown(true);
      }
    };
    t25 = [shouldShowBackdrop];
    $[37] = shouldShowBackdrop;
    $[38] = t24;
    $[39] = t25;
  } else {
    t24 = $[38];
    t25 = $[39];
  }
  useEffect(t24, t25);
  let t26;
  let t27;
  if ($[40] !== activeSectionId) {
    t26 = () => {
      if (activeSectionId === "story") {
        return;
      }
      setHaveStoryClipsFinished(false);
    };
    t27 = [activeSectionId];
    $[40] = activeSectionId;
    $[41] = t26;
    $[42] = t27;
  } else {
    t26 = $[41];
    t27 = $[42];
  }
  useEffect(t26, t27);
  let t28;
  let t29;
  if ($[43] !== isIntroLightningLive) {
    t28 = () => {
      clearStrikeHoldTimer();
      setIsIntroStrikeHeld(isIntroLightningLive);
      return clearStrikeHoldTimer;
    };
    t29 = [isIntroLightningLive, clearStrikeHoldTimer];
    $[43] = isIntroLightningLive;
    $[44] = t28;
    $[45] = t29;
  } else {
    t28 = $[44];
    t29 = $[45];
  }
  useEffect(t28, t29);
  let t30;
  let t31;
  if ($[46] !== isBackdropOwnerOnScreen || $[47] !== playMode || $[48] !== shouldShowBackdrop) {
    t30 = () => {
      const written = backdropWriteRef.current;
      if (!isBackdropOwnerOnScreen) {
        written.value = 0;
        return;
      }
      if (shouldShowBackdrop && playMode === "final") {
        written.value = 1;
        return;
      }
      if (!shouldShowBackdrop) {
        const fadeOut = animate(written, {
          value: 0
        }, {
          duration: SECTION_ATMOSPHERE_FADE_OUT_SECONDS,
          ease: "easeIn"
        });
        return () => fadeOut.stop();
      }
      setBackdropGeneration(_temp6);
      const fadeIn = animate(written, {
        value: 1
      }, {
        duration: BACKDROP_FADE_IN_SECONDS,
        ease: "easeOut"
      });
      return () => fadeIn.stop();
    };
    t31 = [shouldShowBackdrop, playMode, isBackdropOwnerOnScreen];
    $[46] = isBackdropOwnerOnScreen;
    $[47] = playMode;
    $[48] = shouldShowBackdrop;
    $[49] = t30;
    $[50] = t31;
  } else {
    t30 = $[49];
    t31 = $[50];
  }
  useEffect(t30, t31);
  let t32;
  if ($[51] !== activeSectionId || $[52] !== playMode) {
    t32 = () => {
      if (activeSectionId !== "story" || playMode === "final") {
        return;
      }
      const timer = window.setTimeout(() => setStoryVideoGeneration(_temp7), STORY_CLIPS_DELAY_SECONDS * 1000);
      return () => window.clearTimeout(timer);
    };
    $[51] = activeSectionId;
    $[52] = playMode;
    $[53] = t32;
  } else {
    t32 = $[53];
  }
  let t33;
  if ($[54] !== activeSectionId || $[55] !== playMode || $[56] !== storyGeneration) {
    t33 = [activeSectionId, storyGeneration, playMode];
    $[54] = activeSectionId;
    $[55] = playMode;
    $[56] = storyGeneration;
    $[57] = t33;
  } else {
    t33 = $[57];
  }
  useEffect(t32, t33);
  let t34;
  let t35;
  let t36;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = cn("where-span:text-inline-wrap relative w-full overflow-hidden", SECTION_FULL_HEIGHT_CLASSNAME);
    t35 = createDataAttributes({
      test: SPARTAN_ENGINE_HERO_TRACKING.section
    });
    t36 = <SpartanStableViewport sectionId={SPARTAN_ENGINE_HERO_BANNER_SECTION_ID} />;
    $[58] = t34;
    $[59] = t35;
    $[60] = t36;
  } else {
    t34 = $[58];
    t35 = $[59];
    t36 = $[60];
  }
  const t37 = shouldRenderFallback ? BACKGROUND_IMAGE_FALLBACK_SRC : BACKGROUND_IMAGE_SRC;
  let t38;
  if ($[61] !== reportAssetLoaded) {
    t38 = () => reportAssetLoaded("background");
    $[61] = reportAssetLoaded;
    $[62] = t38;
  } else {
    t38 = $[62];
  }
  let t39;
  if ($[63] !== t37 || $[64] !== t38) {
    t39 = <div className="absolute inset-0"><AppImage src={t37} sizes="60vw" alt="" fill={true} onLoad={t38} /></div>;
    $[63] = t37;
    $[64] = t38;
    $[65] = t39;
  } else {
    t39 = $[65];
  }
  const t40 = blackMode === "stage";
  let t41;
  if ($[66] !== t40) {
    t41 = <BlackLayer isVisible={t40} durationSeconds={BLACK_STAGE_FADE_SECONDS} />;
    $[66] = t40;
    $[67] = t41;
  } else {
    t41 = $[67];
  }
  let t42;
  if ($[68] !== activeSectionId || $[69] !== auraResetKey || $[70] !== backdropBurstKey || $[71] !== backdropGeneration || $[72] !== backdropVisibilityRef || $[73] !== engineAnchorRef || $[74] !== engineAuraVisibilityRef || $[75] !== hasBackdropBeenShown || $[76] !== hasSceneCompiled || $[77] !== haveStoryClipsFinished || $[78] !== introReveal || $[79] !== isCanvasPaused || $[80] !== isIntroLightningLive || $[81] !== isIntroStrikeHeld || $[82] !== reducedMotion || $[83] !== shouldRenderFallback) {
    t42 = !shouldRenderFallback && <SpartanWebGPUCanvasBasic paused={isCanvasPaused} readyToCompile={true} onShaderCompiled={() => setHasSceneCompiled(true)} onShaderCompileError={() => setHasSceneCompiled(true)}><EngineBackdrop burstKey={backdropBurstKey} anchorRef={engineAnchorRef} isAnimating={!reducedMotion} visibilityRef={backdropVisibilityRef} replayKey={backdropGeneration} warmUp={!hasBackdropBeenShown} /><EngineStage anchorRef={engineAnchorRef}><EngineAirflow config={ENGINE_AIRFLOW_CONFIG} enabled={activeSectionId === "story" && haveStoryClipsFinished} visibilityRef={engineAuraVisibilityRef} isAnimating={!reducedMotion} warmUp={!hasSceneCompiled} /><EngineAura introFloatAnimationRef={introFloatAnimationRef} storyBreathingAnimationRef={storyBreathingAnimationRef} isAnimating={!reducedMotion} isStory={activeSectionId === "story"} visibilityRef={engineAuraVisibilityRef} resetKey={auraResetKey} warmUp={!hasSceneCompiled} /><group visible={activeSectionId === "intro"}><EngineLightning config={LIGHTNING_AUTHORED_CONFIG} selectedId={LIGHTNING_AUTHORED_CONFIG.groups[0].id} random={true} enabled={isIntroLightningLive} openingShape="together" hold={isIntroStrikeHeld} replay={introReveal} guides={false} frozen={!!reducedMotion} speed={INTRO_LIGHTNING_SPEED} /></group></EngineStage></SpartanWebGPUCanvasBasic>;
    $[68] = activeSectionId;
    $[69] = auraResetKey;
    $[70] = backdropBurstKey;
    $[71] = backdropGeneration;
    $[72] = backdropVisibilityRef;
    $[73] = engineAnchorRef;
    $[74] = engineAuraVisibilityRef;
    $[75] = hasBackdropBeenShown;
    $[76] = hasSceneCompiled;
    $[77] = haveStoryClipsFinished;
    $[78] = introReveal;
    $[79] = isCanvasPaused;
    $[80] = isIntroLightningLive;
    $[81] = isIntroStrikeHeld;
    $[82] = reducedMotion;
    $[83] = shouldRenderFallback;
    $[84] = t42;
  } else {
    t42 = $[84];
  }
  let t43;
  if ($[85] !== t42) {
    t43 = <div ref={canvasBoxRef} className="pointer-events-none absolute inset-0">{t42}</div>;
    $[85] = t42;
    $[86] = t43;
  } else {
    t43 = $[86];
  }
  let t44;
  if ($[87] !== isSkipCovered) {
    t44 = <SectionResetVeil isVisible={isSkipCovered} />;
    $[87] = isSkipCovered;
    $[88] = t44;
  } else {
    t44 = $[88];
  }
  let t45;
  if ($[89] !== isLocked) {
    t45 = cn("absolute inset-0", {
      "touch-none": isLocked
    });
    $[89] = isLocked;
    $[90] = t45;
  } else {
    t45 = $[90];
  }
  let t46;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = () => setHaveStoryClipsFinished(true);
    $[91] = t46;
  } else {
    t46 = $[91];
  }
  const t47 = autoplayCheckedVisit === activeVisitKey;
  let t48;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = () => setPlayBlockedVisit(null);
    $[92] = t48;
  } else {
    t48 = $[92];
  }
  let t49;
  let t50;
  if ($[93] !== reportAssetLoaded) {
    t49 = () => {
      reportAssetLoaded("introVideo");
    };
    t50 = () => {
      reportAssetLoaded("introVideo");
    };
    $[93] = reportAssetLoaded;
    $[94] = t49;
    $[95] = t50;
  } else {
    t49 = $[94];
    t50 = $[95];
  }
  let t51;
  if ($[96] !== introReveal) {
    t51 = () => {
      setVideoEndedGeneration(introReveal);
    };
    $[96] = introReveal;
    $[97] = t51;
  } else {
    t51 = $[97];
  }
  let t52;
  let t53;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = () => setBackdropBurstKey(_temp8);
    t53 = () => {
      handleIntroSettled();
    };
    $[98] = t52;
    $[99] = t53;
  } else {
    t52 = $[98];
    t53 = $[99];
  }
  let t54;
  if ($[100] !== activeIndex || $[101] !== clockStartMs || $[102] !== engineAnchorRef || $[103] !== engineAuraVisibilityRef || $[104] !== exitingIndex || $[105] !== generation || $[106] !== goTo || $[107] !== handleSkip || $[108] !== handleVideoBlocked || $[109] !== haveStoryClipsFinished || $[110] !== isHeroReady || $[111] !== isLocked || $[112] !== isPlayPromptShown || $[113] !== isSectionInView || $[114] !== isSkipCovered || $[115] !== playMode || $[116] !== reportAnimationEnd || $[117] !== reportAssetLoaded || $[118] !== reportExitEnd || $[119] !== shouldRenderFallback || $[120] !== skip || $[121] !== storyVideoGeneration || $[122] !== t47 || $[123] !== t49 || $[124] !== t50 || $[125] !== t51 || $[126] !== videoProgressRef) {
    t54 = <SectionSlider shouldRenderFallback={shouldRenderFallback} canvasBoxRef={canvasBoxRef} engineAnchorRef={engineAnchorRef} engineAuraVisibilityRef={engineAuraVisibilityRef} storyVideoGeneration={storyVideoGeneration} onStoryClipsComplete={t46} haveStoryClipsFinished={haveStoryClipsFinished} isSectionInView={isSectionInView} activeIndex={activeIndex} exitingIndex={exitingIndex} playMode={playMode} videoProgressRef={videoProgressRef} generation={generation} isLocked={isLocked} isSkipCovered={isSkipCovered} onSkip={handleSkip} onVideoLoadTimeout={skip} onVideoBlocked={handleVideoBlocked} isPlayPromptShown={isPlayPromptShown} isAutoplayChecked={t47} onPlayPromptAccepted={t48} clockStartMs={clockStartMs} reportAnimationEnd={reportAnimationEnd} reportExitEnd={reportExitEnd} onSelectSection={goTo} onVideoReady={t49} onVideoError={t50} onIntroImageLoaded={reportAssetLoaded} readyToPlay={isHeroReady} onIntroVideoEnded={t51} introFloatAnimationRef={introFloatAnimationRef} storyBreathingAnimationRef={storyBreathingAnimationRef} onIntroFloatCycle={t52} onIntroSequenceComplete={t53} onBlackModeChange={setBlackMode} onHotspotPopupChange={setIsHotspotPopupOpen} />;
    $[100] = activeIndex;
    $[101] = clockStartMs;
    $[102] = engineAnchorRef;
    $[103] = engineAuraVisibilityRef;
    $[104] = exitingIndex;
    $[105] = generation;
    $[106] = goTo;
    $[107] = handleSkip;
    $[108] = handleVideoBlocked;
    $[109] = haveStoryClipsFinished;
    $[110] = isHeroReady;
    $[111] = isLocked;
    $[112] = isPlayPromptShown;
    $[113] = isSectionInView;
    $[114] = isSkipCovered;
    $[115] = playMode;
    $[116] = reportAnimationEnd;
    $[117] = reportAssetLoaded;
    $[118] = reportExitEnd;
    $[119] = shouldRenderFallback;
    $[120] = skip;
    $[121] = storyVideoGeneration;
    $[122] = t47;
    $[123] = t49;
    $[124] = t50;
    $[125] = t51;
    $[126] = videoProgressRef;
    $[127] = t54;
  } else {
    t54 = $[127];
  }
  let t55;
  if ($[128] !== t45 || $[129] !== t54) {
    t55 = <div ref={gestureSurfaceRef} className={t45}>{t54}</div>;
    $[128] = t45;
    $[129] = t54;
    $[130] = t55;
  } else {
    t55 = $[130];
  }
  const t56 = blackMode === "cover";
  let t57;
  if ($[131] !== t56) {
    t57 = <BlackLayer isVisible={t56} durationSeconds={BLACK_COVER_FADE_SECONDS} />;
    $[131] = t56;
    $[132] = t57;
  } else {
    t57 = $[132];
  }
  const t58 = !isHeroReady;
  let t59;
  if ($[133] !== t58) {
    t59 = <LoadingVeil isVisible={t58} />;
    $[133] = t58;
    $[134] = t59;
  } else {
    t59 = $[134];
  }
  let t60;
  if ($[135] !== sectionRef || $[136] !== t39 || $[137] !== t41 || $[138] !== t43 || $[139] !== t44 || $[140] !== t55 || $[141] !== t57 || $[142] !== t59) {
    t60 = <section ref={sectionRef} id={SPARTAN_ENGINE_HERO_BANNER_SECTION_ID} className={t34} {...t35}>{t36}{t39}{t41}{t43}{t44}{t55}{t57}{t59}</section>;
    $[135] = sectionRef;
    $[136] = t39;
    $[137] = t41;
    $[138] = t43;
    $[139] = t44;
    $[140] = t55;
    $[141] = t57;
    $[142] = t59;
    $[143] = t60;
  } else {
    t60 = $[143];
  }
  return t60;
};
function _temp() {
  return 0;
}
function _temp2() {
  return 0;
}
function _temp3() {
  return createEngineAnchor();
}
function _temp4(value) {
  return value + 1;
}
function _temp5(value_0) {
  return value_0 + 1;
}
function _temp6(value_1) {
  return value_1 + 1;
}
function _temp7(value_2) {
  return value_2 + 1;
}
function _temp8(value_3) {
  return value_3 + 1;
}